// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const Sidebar = styled.div`
  width: ${props => (props.expanded ? '220px' : '60px')};
  background-color: #151e3d;
  height: 90vh;
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  padding-top: 20px;
  position: fixed;
  left: 0;
  top: 10vh;
  z-index: 6;

  @media (max-width: 768px) {
    width: ${props => (props.expanded ? '180px' : '50px')};
    height: 93vh;
    top: 7vh;
  }

  @media (max-width: 480px) {
    width: ${props => (props.expanded ? '150px' : '50px')};
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ToggleButton = styled.div`
  cursor: pointer;
  font-size: 24px;
  text-align: center;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    display: none;
  }
`;


const SidebarItem = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  font-weight: ${props => (props.active ? 'bold' : 'normal')};

  &:hover {
    background-color: #3b486d;
  }

  &:hover > div {
    display: block;
  }

  @media (max-width: 768px) {
    padding: 10px;
    justify-content: center;
  }
`;

const SidebarIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: ${props => (props.expanded ? '10px' : '0')};
  filter: invert(1);

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const SidebarLabel = styled.span`
  display: ${props => (props.expanded ? 'inline' : 'none')};

  @media (max-width: 768px) {
    display: ${props => (props.expanded ? 'inline' : 'none')};
  }
`;

const Submenu = styled.div`
  display: none;
  background-color: #3b486d;
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 160px;
  z-index: 1;
  flex-direction: column;

  @media (max-width: 768px) {
    left: 0;
    top: auto;
    min-width: 100%;
  }
`;

const SubmenuItem = styled.div`
  padding: 10px;
  cursor: pointer;

  &:hover {
    background-color: #aeb4c9;
  }
`;

const LogoutButton = styled.div`
  margin: 5vh 5px;
  padding: 10px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.expanded ? 'flex-start' : 'center')};
  border-radius: 4px;
  color: white;
  transition: background-color 0.3s ease, transform 0.2s ease;
  margin-top: auto;

  &:hover {
    background-color: lightcoral;
    color: white;
  }

  &:active {
    transform: scale(0.98);
    background-color: darkred;
  }

  @media (max-width: 768px) {
    margin: 15vh 5px;
    font-size: 12px;
    padding: 5px 8px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 5px 7px;
  }
`;

const LogoutIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  filter: invert(1);
`;

const SidebarComponent = ({ sidebarExpanded, setSidebarExpanded, setSelectedScreen }) => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    if (isMobile && sidebarExpanded) {
      setSidebarExpanded(false);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, sidebarExpanded, setSidebarExpanded]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <Sidebar expanded={!isMobile && sidebarExpanded}>
      <SidebarContent>
        {!isMobile && (
          <ToggleButton onClick={() => setSidebarExpanded(!sidebarExpanded)}>
            ☰
          </ToggleButton>
        )}

        <SidebarItem onClick={() => setSelectedScreen('dashboard')}>
          <SidebarIcon src="/dashboard.png" alt="Dashboard" expanded={!isMobile && sidebarExpanded} />
          <SidebarLabel expanded={!isMobile && sidebarExpanded}>Dashboard</SidebarLabel>
        </SidebarItem>

        <SidebarItem>
          <SidebarIcon src="/cadastro.png" alt="Cadastro" expanded={!isMobile && sidebarExpanded} />
          <SidebarLabel expanded={!isMobile && sidebarExpanded}>Cadastros</SidebarLabel>
          <Submenu>
            {role === 'admin' && (
              <>
                <SubmenuItem onClick={() => setSelectedScreen('contas')}>Contas a Pagar</SubmenuItem>
                <SubmenuItem onClick={() => setSelectedScreen('users')}>Usuários</SubmenuItem>
              </>
            )}
            <SubmenuItem onClick={() => setSelectedScreen('clients')}>Clientes</SubmenuItem>
            <SubmenuItem onClick={() => setSelectedScreen('onibus')}>Onibus</SubmenuItem>
          </Submenu>
        </SidebarItem>

        <SidebarItem>
          <SidebarIcon src="/roteiro.png" alt="Roteiro" expanded={!isMobile && sidebarExpanded} />
          <SidebarLabel expanded={!isMobile && sidebarExpanded}>Roteiro</SidebarLabel>
          <Submenu>
            <SubmenuItem onClick={() => setSelectedScreen('viagens')}>Viagens</SubmenuItem>
          </Submenu>
        </SidebarItem>
        <SidebarItem>
          <SidebarIcon src="/agenda.png" alt="Agenda" expanded={!isMobile && sidebarExpanded} />
          <SidebarLabel expanded={!isMobile && sidebarExpanded}>Agenda</SidebarLabel>
          <Submenu>
            <SubmenuItem onClick={() => setSelectedScreen('agenda')}>Agenda</SubmenuItem>
          </Submenu>
        </SidebarItem>
      </SidebarContent>

      <LogoutButton expanded={!isMobile && sidebarExpanded} onClick={handleLogout}>
        <LogoutIcon src="/logout.png" alt="Logout" />
        <SidebarLabel expanded={!isMobile && sidebarExpanded}>Deslogar</SidebarLabel>
      </LogoutButton>
    </Sidebar>
  );
};

export default SidebarComponent;
